<script setup lang="ts">
import {
  provide,
  shallowReactive,
  markRaw,
  type Component,
  type ComponentPublicInstance,
  onMounted,
  onUnmounted,
  ref,
  watch,
  nextTick,
} from 'vue'
import { DIALOG_CONTEXT, type DialogOptions, type DialogItem, type DialogContextType } from './dialog.types'

// Track active dialogs
const dialogItems = shallowReactive<DialogItem[]>([])
const dialogPanelRefs = ref<Record<string, HTMLElement | null>>({})
const previouslyFocusedElements = new Map<string, HTMLElement>()

// Create the dialog context
const dialogContext: DialogContextType = {
  items: dialogItems,

  register<T extends Component>(options: DialogOptions<T>): string {
    const id = crypto.randomUUID()
    dialogItems.push({
      id,
      component: markRaw(options.component),
      props: options.props,
      onClose: options.onClose,
    })
    return id
  },

  unregister(id: string): void {
    const index = dialogItems.findIndex((item) => item.id === id)
    if (index !== -1) {
      const [item] = dialogItems.splice(index, 1)
      item?.onClose?.()
    }
  },
}

// Provide the dialog context
provide(DIALOG_CONTEXT, dialogContext)

function handleBackdropClick(): void {
  // Close the topmost dialog (last in array)
  if (dialogItems.length > 0) {
    const topDialog = dialogItems[dialogItems.length - 1]
    if (!topDialog) return

    dialogContext.unregister(topDialog.id)
  }
}

function setDialogPanelRef(id: string, element: Element | ComponentPublicInstance | null): void {
  dialogPanelRefs.value[id] = element instanceof HTMLElement ? element : null
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(','),
    ),
  ).filter((element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true')
}

function focusDialog(id: string): void {
  const panel = dialogPanelRefs.value[id]
  if (!panel) return

  const firstFocusable = getFocusableElements(panel)[0]
  ;(firstFocusable ?? panel).focus()
}

function trapTabKey(event: KeyboardEvent, container: HTMLElement): void {
  const focusableElements = getFocusableElements(container)

  if (focusableElements.length === 0) {
    event.preventDefault()
    container.focus()
    return
  }

  const firstElement = focusableElements[0]
  const lastElement = focusableElements[focusableElements.length - 1]

  if (!firstElement || !lastElement) return

  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault()
    lastElement.focus()
    return
  }

  if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault()
    firstElement.focus()
  }
}

// ESC key handler to close topmost dialog
function handleKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape' && dialogItems.length > 0) {
    const topDialog = dialogItems[dialogItems.length - 1]
    if (!topDialog) return

    dialogContext.unregister(topDialog.id)
  }

  if (e.key === 'Tab' && dialogItems.length > 0) {
    const topDialog = dialogItems[dialogItems.length - 1]
    if (!topDialog) return

    const panel = dialogPanelRefs.value[topDialog.id]
    if (!panel) return

    trapTabKey(e, panel)
  }
}

watch(
  () => dialogItems.map((item) => item.id),
  async (currentIds, previousIds) => {
    const openedId = currentIds.find((id) => !previousIds.includes(id))
    if (openedId) {
      const activeElement = document.activeElement
      if (activeElement instanceof HTMLElement) {
        previouslyFocusedElements.set(openedId, activeElement)
      }

      await nextTick()
      focusDialog(openedId)
      return
    }

    const closedId = previousIds.find((id) => !currentIds.includes(id))
    if (closedId) {
      const elementToRestore = previouslyFocusedElements.get(closedId)
      previouslyFocusedElements.delete(closedId)
      delete dialogPanelRefs.value[closedId]
      elementToRestore?.focus()
    }
  },
)

// Register global ESC key listener
onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  for (const item of [...dialogItems]) dialogContext.unregister(item.id)
})
</script>

<template>
  <slot />

  <!--
    Dialogs stack above sticky headers (z-50) and side drawers (z-60/70) so the
    whole page dims behind them; toasts (z-100) stay readable on top.
  -->
  <!-- Single backdrop for all dialogs (shown when any dialog is open) -->
  <Transition name="dialog-backdrop">
    <div v-if="dialogItems.length > 0" class="dialog-backdrop fixed inset-0 bg-black/50 backdrop-blur-sm" @click="handleBackdropClick" />
  </Transition>

  <!-- Render dialogs on top of backdrop -->
  <TransitionGroup name="dialog-panel">
    <div
      v-for="item in dialogItems"
      :key="item.id"
      :ref="(element) => setDialogPanelRef(item.id, element)"
      class="dialog-panel fixed inset-0 flex items-center justify-center p-4"
      role="dialog"
      aria-label="Dialog"
      aria-modal="true"
      tabindex="-1"
      @click.self="dialogContext.unregister(item.id)"
    >
      <component
        :is="item.component"
        v-bind="item.props || {}"
        :dialog-id="item.id"
        @close="dialogContext.unregister(item.id)"
        @click.stop
      />
    </div>
  </TransitionGroup>
</template>

<style scoped>
.dialog-backdrop {
  z-index: 80;
}

.dialog-panel {
  z-index: 90;
}

.dialog-backdrop-enter-active,
.dialog-panel-enter-active {
  transition:
    opacity 180ms cubic-bezier(0, 0, 0.2, 1),
    transform 180ms cubic-bezier(0, 0, 0.2, 1);
}

.dialog-backdrop-leave-active,
.dialog-panel-leave-active {
  transition:
    opacity 120ms cubic-bezier(0.4, 0, 1, 1),
    transform 120ms cubic-bezier(0.4, 0, 1, 1);
}

.dialog-backdrop-enter-from,
.dialog-backdrop-leave-to,
.dialog-panel-enter-from,
.dialog-panel-leave-to {
  opacity: 0;
}

.dialog-panel-enter-from,
.dialog-panel-leave-to {
  transform: scale(0.98);
}

@media (prefers-reduced-motion: reduce) {
  .dialog-backdrop-enter-active,
  .dialog-backdrop-leave-active,
  .dialog-panel-enter-active,
  .dialog-panel-leave-active {
    transition: none;
  }
}
</style>
